import React, { useState, useEffect, useCallback } from 'react';
import axios from './../axios';
import {
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Avatar,
    Divider,
    IconButton,
    Tabs,
    Tab,
    CircularProgress,
    Button,
    TextField,
    Alert,
    Snackbar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack,
    LocationOn,
    Person,
    Email,
    Phone,
    CalendarToday,
    Agriculture,
    Map,
    AccountCircle,
    Edit,
    Save,
    Cancel,
    CheckCircle,
    ErrorOutline,
    VpnKey
} from '@mui/icons-material';
import Spinner from './Spinner';

// Location Display Component with specific, hierarchical location information
const LocationDisplay = ({ farmer }) => {
    const [locationData, setLocationData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchLocationName = async () => {
            // If farmer already has a location name, use it
            if (farmer.location && farmer.location !== 'N/A') {
                setLocationData({ primary: farmer.location, details: null });
                return;
            }

            // If no coordinates, show N/A
            if (!farmer.coordinates || !farmer.coordinates.lat || !farmer.coordinates.lng) {
                setLocationData({ primary: 'N/A', details: null });
                return;
            }

            setLoading(true);
            try {
                // Add a small delay to respect rate limits (max 1 request per second)
                await new Promise((resolve) => setTimeout(resolve, 1000));

                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${farmer.coordinates.lat}&lon=${farmer.coordinates.lng}&addressdetails=1&accept-language=en&zoom=18`,
                    {
                        headers: {
                            'User-Agent': 'YourAppName/1.0' // Replace with your app name
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Geocoding request failed');
                }

                const data = await response.json();

                if (data && data.address) {
                    const address = data.address;

                    // Build hierarchical location information for Uganda
                    const locationInfo = {
                        // Most specific location (house/building/road)
                        specific:
                            address.house_number && address.road
                                ? `${address.house_number} ${address.road}`
                                : address.road ||
                                  address.footway ||
                                  address.path ||
                                  address.pedestrian,

                        // Neighborhood/suburb/locality/zone
                        neighborhood:
                            address.neighbourhood ||
                            address.suburb ||
                            address.hamlet ||
                            address.locality ||
                            address.quarter ||
                            address.residential,

                        // Village/town/city/trading center
                        village: address.village || address.hamlet,
                        town: address.town,
                        city: address.city,
                        settlement: address.village || address.town || address.city,

                        // Uganda-specific administrative divisions
                        parish: address.parish || address.municipality,
                        subcounty:
                            address.subcounty || address.county_district || address.administrative,
                        division: address.city_district || address.borough || address.municipality,
                        county: address.county,
                        district: address.district || address.state_district,
                        region: address.state || address.region || address.province,
                        country: address.country,

                        // Additional details
                        postcode: address.postcode,
                        constituency: address.constituency
                    };

                    // Build primary location string (most relevant info for Uganda)
                    const primaryParts = [];

                    // Start with most specific available location
                    if (locationInfo.specific) primaryParts.push(locationInfo.specific);
                    if (locationInfo.neighborhood) primaryParts.push(locationInfo.neighborhood);

                    // Add village, town, or city
                    if (locationInfo.village) {
                        primaryParts.push(locationInfo.village + ' Village');
                    } else if (locationInfo.town) {
                        primaryParts.push(locationInfo.town + ' Town');
                    } else if (locationInfo.city) {
                        primaryParts.push(locationInfo.city + ' City');
                    }

                    // If no specific parts, use broader administrative areas
                    if (primaryParts.length === 0) {
                        if (locationInfo.parish) primaryParts.push(locationInfo.parish + ' Parish');
                        if (locationInfo.subcounty)
                            primaryParts.push(locationInfo.subcounty + ' Subcounty');
                    }

                    // Build secondary location string (administrative hierarchy for Uganda)
                    const secondaryParts = [];

                    // Add division (for cities like Kampala)
                    if (
                        locationInfo.division &&
                        !primaryParts.some((part) => part.includes(locationInfo.division))
                    ) {
                        secondaryParts.push(locationInfo.division + ' Division');
                    }

                    // Add parish if not already included
                    if (
                        locationInfo.parish &&
                        !primaryParts.some((part) => part.includes(locationInfo.parish))
                    ) {
                        secondaryParts.push(locationInfo.parish + ' Parish');
                    }

                    // Add subcounty if not already included
                    if (
                        locationInfo.subcounty &&
                        !primaryParts.some((part) => part.includes(locationInfo.subcounty))
                    ) {
                        secondaryParts.push(locationInfo.subcounty + ' Subcounty');
                    }

                    // Add county if available and not included
                    if (
                        locationInfo.county &&
                        !secondaryParts.some((part) => part.includes(locationInfo.county))
                    ) {
                        secondaryParts.push(locationInfo.county + ' County');
                    }

                    // Add district
                    if (locationInfo.district) {
                        secondaryParts.push(locationInfo.district + ' District');
                    }

                    // Add region
                    if (locationInfo.region && locationInfo.region !== locationInfo.district) {
                        secondaryParts.push(locationInfo.region + ' Region');
                    }

                    // Add country if not Uganda (for border areas)
                    if (locationInfo.country && locationInfo.country !== 'Uganda') {
                        secondaryParts.push(locationInfo.country);
                    }

                    const primary =
                        primaryParts.length > 0 ? primaryParts.join(', ') : data.display_name;
                    const secondary = secondaryParts.length > 0 ? secondaryParts.join(', ') : null;

                    setLocationData({
                        primary,
                        secondary,
                        details: locationInfo,
                        fullAddress: data.display_name
                    });
                } else {
                    setLocationData({
                        primary: data.display_name || 'Unknown Location',
                        details: null
                    });
                }
            } catch (error) {
                console.error('Geocoding error:', error);
                setLocationData({ primary: 'Geocoding failed', details: null });
            } finally {
                setLoading(false);
            }
        };

        fetchLocationName();
    }, [farmer]);

    if (loading) {
        return (
            <Box display='flex' alignItems='center' gap={1}>
                <CircularProgress size={16} />
                <Typography variant='body2' color='text.secondary'>
                    Loading location...
                </Typography>
            </Box>
        );
    }

    if (!locationData) {
        return <Typography variant='body2'>N/A</Typography>;
    }

    return (
        <Box>
            <Typography variant='body2' fontWeight='medium'>
                {locationData.primary}
            </Typography>
            {locationData.secondary && (
                <Typography variant='caption' color='text.secondary' display='block'>
                    {locationData.secondary}
                </Typography>
            )}
            {locationData.details?.postcode && (
                <Typography variant='caption' color='primary' display='block'>
                    Postal: {locationData.details.postcode}
                </Typography>
            )}
            {farmer.coordinates && (
                <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mt: 0.5 }}
                >
                    {farmer.coordinates.lat.toFixed(6)}, {farmer.coordinates.lng.toFixed(6)}
                </Typography>
            )}
        </Box>
    );
};

// Enhanced Location History Card Component with specific location details
const LocationHistoryCard = ({ location, index }) => {
    const [locationData, setLocationData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchLocationName = async () => {
            if (location.address) {
                setLocationData({ primary: location.address, details: null });
                return;
            }

            if (!location.coordinates || !location.coordinates.lat || !location.coordinates.lng) {
                setLocationData({ primary: 'Unknown Location', details: null });
                return;
            }

            setLoading(true);
            try {
                await new Promise((resolve) => setTimeout(resolve, 1000 * index)); // Stagger requests

                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.coordinates.lat}&lon=${location.coordinates.lng}&addressdetails=1&accept-language=en&zoom=18`,
                    {
                        headers: {
                            'User-Agent': 'YourAppName/1.0'
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Geocoding request failed');
                }

                const data = await response.json();

                if (data && data.address) {
                    const address = data.address;

                    // Build comprehensive location information for Uganda
                    const locationInfo = {
                        specific:
                            address.house_number && address.road
                                ? `${address.house_number} ${address.road}`
                                : address.road ||
                                  address.footway ||
                                  address.path ||
                                  address.pedestrian,
                        neighborhood:
                            address.neighbourhood ||
                            address.suburb ||
                            address.hamlet ||
                            address.locality ||
                            address.quarter,
                        village: address.village || address.hamlet,
                        town: address.town,
                        city: address.city,
                        parish: address.parish || address.municipality,
                        subcounty:
                            address.subcounty || address.county_district || address.administrative,
                        division: address.city_district || address.borough || address.municipality,
                        county: address.county,
                        district: address.district || address.state_district,
                        region: address.state || address.region,
                        country: address.country,
                        postcode: address.postcode
                    };

                    // Primary location (most specific available)
                    const primaryParts = [];
                    if (locationInfo.specific) primaryParts.push(locationInfo.specific);
                    if (locationInfo.neighborhood) primaryParts.push(locationInfo.neighborhood);

                    // Add settlement type with proper labeling
                    if (locationInfo.village) {
                        primaryParts.push(locationInfo.village + ' Village');
                    } else if (locationInfo.town) {
                        primaryParts.push(locationInfo.town + ' Town');
                    } else if (locationInfo.city) {
                        primaryParts.push(locationInfo.city + ' City');
                    }

                    if (primaryParts.length === 0) {
                        if (locationInfo.parish) primaryParts.push(locationInfo.parish + ' Parish');
                        if (locationInfo.subcounty)
                            primaryParts.push(locationInfo.subcounty + ' Subcounty');
                    }

                    // Administrative hierarchy for Uganda
                    const adminParts = [];

                    // Division (for major cities)
                    if (
                        locationInfo.division &&
                        !primaryParts.some((part) => part.includes(locationInfo.division))
                    ) {
                        adminParts.push(locationInfo.division + ' Division');
                    }

                    // Parish
                    if (
                        locationInfo.parish &&
                        !primaryParts.some((part) => part.includes(locationInfo.parish))
                    ) {
                        adminParts.push(locationInfo.parish + ' Parish');
                    }

                    // Subcounty
                    if (
                        locationInfo.subcounty &&
                        !primaryParts.some((part) => part.includes(locationInfo.subcounty))
                    ) {
                        adminParts.push(locationInfo.subcounty + ' Subcounty');
                    }

                    // County
                    if (
                        locationInfo.county &&
                        !adminParts.some((part) => part.includes(locationInfo.county))
                    ) {
                        adminParts.push(locationInfo.county + ' County');
                    }

                    // District
                    if (locationInfo.district) {
                        adminParts.push(locationInfo.district + ' District');
                    }

                    // Region
                    if (locationInfo.region && locationInfo.region !== locationInfo.district) {
                        adminParts.push(locationInfo.region + ' Region');
                    }

                    setLocationData({
                        primary:
                            primaryParts.length > 0 ? primaryParts.join(', ') : data.display_name,
                        administrative: adminParts.length > 0 ? adminParts.join(', ') : null,
                        details: locationInfo,
                        fullAddress: data.display_name
                    });
                } else {
                    setLocationData({
                        primary: data.display_name || 'Unknown Location',
                        details: null
                    });
                }
            } catch (error) {
                console.error('Geocoding error:', error);
                setLocationData({ primary: 'Geocoding failed', details: null });
            } finally {
                setLoading(false);
            }
        };

        fetchLocationName();
    }, [location, index]);

    return (
        <Card variant='outlined'>
            <CardContent>
                <Box display='flex' alignItems='flex-start' gap={2}>
                    <LocationOn color='primary' sx={{ mt: 0.5 }} />
                    <Box flex={1}>
                        <Box display='flex' alignItems='center' gap={1} mb={1}>
                            {loading && <CircularProgress size={16} />}
                            <Typography variant='subtitle1' fontWeight='medium'>
                                {locationData?.primary || 'Loading location...'}
                            </Typography>
                        </Box>

                        {locationData?.administrative && (
                            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                                📍 {locationData.administrative} |{' '}
                                {new Date(location.timestamp).toLocaleString()} |{' '}
                                {location.coordinates.lat.toFixed(6)},{' '}
                                {location.coordinates.lng.toFixed(6)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const AgentDetailView = ({ agentId, onBack }) => {
    const [agent, setAgent] = useState(null);
    const [farmers, setFarmers] = useState([]);
    const [locationHistory, setLocationHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    // Edit states
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        email: '',
        contact: ''
    });
    const [editLoading, setEditLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Pagination states (for farmers table)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Location history states
    const [locationPage, setLocationPage] = useState(0);
    const [locationRowsPerPage, setLocationRowsPerPage] = useState(10);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationPagination, setLocationPagination] = useState({
        total: 0,
        hasMore: false
    });
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    });

    // Password reset states
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    const fetchLocationHistory = useCallback(
        async (pageNum = 0, pageSize = locationRowsPerPage, filters = dateFilter) => {
            setLocationLoading(true);
            try {
                const params = new URLSearchParams({
                    limit: pageSize.toString(),
                    skip: (pageNum * pageSize).toString()
                });

                // Add date filters if provided
                if (filters.startDate) {
                    params.append('startDate', filters.startDate);
                }
                if (filters.endDate) {
                    params.append('endDate', filters.endDate);
                }

                const locationResponse = await axios.get(
                    `/admin/locations/agent/${agentId}?${params}`
                );
                const data = locationResponse.data;

                if (pageNum === 0) {
                    // First page - replace all data
                    setLocationHistory(data.locations || []);
                } else {
                    // Additional pages - append to existing data
                    setLocationHistory((prev) => [...prev, ...(data.locations || [])]);
                }

                setLocationPagination({
                    total: data.pagination?.total || 0,
                    hasMore: data.pagination?.hasMore || false
                });
            } catch (err) {
                console.log('Error fetching location history:', err);
                setLocationHistory([]);
                setLocationPagination({ total: 0, hasMore: false });
            } finally {
                setLocationLoading(false);
            }
        },
        [agentId, locationRowsPerPage, dateFilter]
    );

    const fetchAgentDetails = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch specific agent details using the new endpoint
            const agentResponse = await axios.get(`/admin/agent/${agentId}`);
            const agentData = agentResponse.data.data;

            setAgent(agentData);
            setEditData({
                email: agentData.email || '',
                contact: agentData.contact || ''
            });

            try {
                // Fetch farmers added by this agent using the adderID
                const farmersResponse = await axios.get(`/farmer/agent/${agentId}`);
                setFarmers(farmersResponse.data.data || []);
            } catch (err) {
                console.log('Error fetching farmers:', err);
                setFarmers([]);
            }

            // Fetch initial location history
            await fetchLocationHistory();

            setLoading(false);
        } catch (err) {
            console.error('Error fetching agent details:', err);
            setError('Failed to fetch agent details');
            setLoading(false);
        }
    }, [agentId, fetchLocationHistory]);

    useEffect(() => {
        fetchAgentDetails();
    }, [agentId, fetchAgentDetails]);

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Location pagination handlers
    const handleLocationChangePage = (event, newPage) => {
        setLocationPage(newPage);
        if (newPage * locationRowsPerPage >= locationHistory.length && locationPagination.hasMore) {
            // Load more data if needed
            const pageToFetch = Math.floor(locationHistory.length / locationRowsPerPage);
            fetchLocationHistory(pageToFetch, locationRowsPerPage);
        }
    };

    const handleLocationChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setLocationRowsPerPage(newRowsPerPage);
        setLocationPage(0);
        // Refetch with new page size
        fetchLocationHistory(0, newRowsPerPage);
    };

    // Date filter handlers
    const handleDateFilterChange = (field, value) => {
        const newFilter = { ...dateFilter, [field]: value };
        setDateFilter(newFilter);
    };

    const applyDateFilter = () => {
        setLocationPage(0);
        fetchLocationHistory(0, locationRowsPerPage, dateFilter);
    };

    const clearDateFilter = () => {
        const clearedFilter = { startDate: '', endDate: '' };
        setDateFilter(clearedFilter);
        setLocationPage(0);
        fetchLocationHistory(0, locationRowsPerPage, clearedFilter);
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Cancel edit - reset data
            setEditData({
                email: agent.email || '',
                contact: agent.contact || ''
            });
        }
        setIsEditing(!isEditing);
    };

    const handleEditChange = (field, value) => {
        setEditData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveEdit = async () => {
        setEditLoading(true);
        try {
            const response = await axios.put(`/admin/agent/${agentId}`, {
                email: editData.email,
                contact: editData.contact
            });

            console.log('Update response:', response.data);

            // Update local agent data
            setAgent((prev) => ({
                ...prev,
                email: editData.email,
                contact: editData.contact
            }));

            setIsEditing(false);
            setSnackbar({
                open: true,
                message: 'Agent contact information updated successfully!',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error updating agent:', err);
            console.error('Error response:', err.response?.data);
            setSnackbar({
                open: true,
                message: `Failed to update agent information: ${
                    err.response?.data?.message || err.message
                }`,
                severity: 'error'
            });
        } finally {
            setEditLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleResetPassword = async () => {
        if (!newPassword || !confirmPassword) {
            setSnackbar({
                open: true,
                message: 'Please fill in all fields',
                severity: 'error'
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            setSnackbar({
                open: true,
                message: 'Passwords do not match',
                severity: 'error'
            });
            return;
        }

        if (newPassword.length < 6) {
            setSnackbar({
                open: true,
                message: 'Password must be at least 6 characters long',
                severity: 'error'
            });
            return;
        }

        setPasswordLoading(true);
        try {
            await axios.post(`/admin/agent/reset-password/${agentId}`, {
                newPassword
            });

            setSnackbar({
                open: true,
                message:
                    'Password reset successfully! Agent will receive an email with the new password.',
                severity: 'success'
            });

            // Reset form and close modal
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordModal(false);
        } catch (error) {
            console.error('Error resetting password:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to reset password',
                severity: 'error'
            });
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleOpenPasswordModal = () => {
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordModal(true);
    };

    const handleClosePasswordModal = () => {
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordModal(false);
    };

    if (loading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' height='80vh'>
                <Spinner size='big' />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' height='80vh'>
                <Typography color='error'>{error}</Typography>
            </Box>
        );
    }

    if (!agent) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' height='80vh'>
                <Typography>Agent not found</Typography>
            </Box>
        );
    }

    return (
        <>
            {/* Header with back button */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    mt: '-24px',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg, #085d40 0%, #047857 100%)',
                    color: 'white'
                }}
            >
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Box display='flex' alignItems='center'>
                        <IconButton
                            onClick={onBack}
                            sx={{
                                mr: 2,
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant='h4' fontWeight='600' sx={{ mb: 0.5 }}>
                                Agent Details
                            </Typography>
                            <Typography variant='body1' sx={{ opacity: 0.9 }}>
                                Comprehensive view of agent information and activity
                            </Typography>
                        </Box>
                    </Box>

                    {/* Edit Button */}
                    {agent && !isEditing && (
                        <Button
                            variant='outlined'
                            startIcon={<Edit />}
                            onClick={handleEditToggle}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.5)',
                                color: 'white',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            Edit Contact Info
                        </Button>
                    )}
                </Box>
            </Paper>
            <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                {/* Two Column Layout */}
                <Grid container spacing={3}>
                    {/* Left Column - Agent Details and Stats */}
                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3
                            }}
                        >
                            {/* Agent Profile Card */}
                            <Card
                                elevation={0}
                                sx={{
                                    flexGrow: 0,
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 3,
                                        background:
                                            'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                                    }}
                                >
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        mb={3}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                bgcolor: '#085d40',
                                                fontSize: '2.5rem',
                                                mb: 2,
                                                boxShadow: '0 8px 24px rgba(8, 93, 64, 0.3)'
                                            }}
                                        >
                                            {agent?.name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Typography
                                            variant='h5'
                                            fontWeight='600'
                                            gutterBottom
                                            align='center'
                                        >
                                            {agent?.name}
                                        </Typography>
                                        <Chip
                                            label={agent?.suspended ? 'Suspended' : 'Active'}
                                            color={agent?.suspended ? 'warning' : 'success'}
                                            sx={{
                                                fontWeight: 'bold',
                                                px: 2,
                                                borderRadius: 2
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <CardContent sx={{ pt: 2 }}>
                                    <Box display='flex' flexDirection='column' gap={3}>
                                        {/* Email Field */}
                                        <Box>
                                            <Box display='flex' alignItems='center' gap={1} mb={1}>
                                                <Email color='action' fontSize='small' />
                                                <Typography
                                                    variant='subtitle2'
                                                    color='text.secondary'
                                                    fontWeight='600'
                                                >
                                                    Email Address
                                                </Typography>
                                            </Box>
                                            {isEditing ? (
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    variant='outlined'
                                                    value={editData.email}
                                                    onChange={(e) =>
                                                        handleEditChange('email', e.target.value)
                                                    }
                                                    placeholder='Enter email address'
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant='body2' sx={{ pl: 3, py: 1 }}>
                                                    {agent?.email || 'No email provided'}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Phone Field */}
                                        <Box>
                                            <Box display='flex' alignItems='center' gap={1} mb={1}>
                                                <Phone color='action' fontSize='small' />
                                                <Typography
                                                    variant='subtitle2'
                                                    color='text.secondary'
                                                    fontWeight='600'
                                                >
                                                    Phone Number
                                                </Typography>
                                            </Box>
                                            {isEditing ? (
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    variant='outlined'
                                                    value={editData.contact}
                                                    onChange={(e) =>
                                                        handleEditChange('contact', e.target.value)
                                                    }
                                                    placeholder='Enter phone number'
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant='body2' sx={{ pl: 3, py: 1 }}>
                                                    {agent?.contact || 'No phone number provided'}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Edit Actions */}
                                        {isEditing && (
                                            <Box display='flex' gap={1} mt={2}>
                                                <Button
                                                    variant='contained'
                                                    size='small'
                                                    startIcon={
                                                        editLoading ? (
                                                            <CircularProgress
                                                                size={16}
                                                                color='inherit'
                                                            />
                                                        ) : (
                                                            <Save />
                                                        )
                                                    }
                                                    onClick={handleSaveEdit}
                                                    disabled={editLoading}
                                                    sx={{
                                                        backgroundColor: '#085d40',
                                                        '&:hover': { backgroundColor: '#047857' },
                                                        borderRadius: 2,
                                                        flex: 1
                                                    }}
                                                >
                                                    {editLoading ? 'Saving...' : 'Save'}
                                                </Button>
                                                <Button
                                                    variant='outlined'
                                                    size='small'
                                                    startIcon={<Cancel />}
                                                    onClick={handleEditToggle}
                                                    disabled={editLoading}
                                                    sx={{
                                                        borderRadius: 2,
                                                        flex: 1
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        )}

                                        {/* Password Reset Button */}
                                        {!isEditing && (
                                            <Box mt={2}>
                                                <Button
                                                    variant='outlined'
                                                    size='small'
                                                    startIcon={<VpnKey />}
                                                    onClick={handleOpenPasswordModal}
                                                    sx={{
                                                        borderColor: '#e11d48',
                                                        color: '#e11d48',
                                                        '&:hover': {
                                                            borderColor: '#be123c',
                                                            backgroundColor:
                                                                'rgba(225, 29, 72, 0.04)'
                                                        },
                                                        borderRadius: 2,
                                                        width: '100%'
                                                    }}
                                                >
                                                    Reset Password
                                                </Button>
                                            </Box>
                                        )}

                                        <Divider sx={{ my: 1 }} />

                                        {/* Other Info */}
                                        <Box display='flex' alignItems='center' gap={1}>
                                            <Person color='action' fontSize='small' />
                                            <Box>
                                                <Typography
                                                    variant='caption'
                                                    color='text.secondary'
                                                    display='block'
                                                >
                                                    Agent ID
                                                </Typography>
                                                <Typography variant='body2' fontFamily='monospace'>
                                                    {agent?._id}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box display='flex' alignItems='center' gap={1}>
                                            <CalendarToday color='action' fontSize='small' />
                                            <Box>
                                                <Typography
                                                    variant='caption'
                                                    color='text.secondary'
                                                    display='block'
                                                >
                                                    Joined Date
                                                </Typography>
                                                <Typography variant='body2'>
                                                    {agent?.createdAt
                                                        ? new Date(
                                                              agent.createdAt
                                                          ).toLocaleDateString()
                                                        : 'Unknown'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Statistics Cards */}
                            <Box display='flex' flexDirection='column' gap={2} flexGrow={1}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                        border: '1px solid #93c5fd',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.15)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box display='flex' alignItems='center' gap={2}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: '#3b82f6',
                                                    color: 'white'
                                                }}
                                            >
                                                <Agriculture sx={{ fontSize: 28 }} />
                                            </Box>
                                            <Box flex={1}>
                                                <Typography
                                                    color='#1e40af'
                                                    variant='body2'
                                                    fontWeight='600'
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    Farmers Added
                                                </Typography>
                                                <Typography
                                                    variant='h3'
                                                    color='#1e40af'
                                                    fontWeight='700'
                                                >
                                                    {farmers.length}
                                                </Typography>
                                                <Typography variant='caption' color='#64748b'>
                                                    Total registered farmers
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card
                                    elevation={0}
                                    sx={{
                                        background:
                                            'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                        border: '1px solid #86efac',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 24px rgba(34, 197, 94, 0.15)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box display='flex' alignItems='center' gap={2}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: '#22c55e',
                                                    color: 'white'
                                                }}
                                            >
                                                <Map sx={{ fontSize: 28 }} />
                                            </Box>
                                            <Box flex={1}>
                                                <Typography
                                                    color='#15803d'
                                                    variant='body2'
                                                    fontWeight='600'
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    Locations Visited
                                                </Typography>
                                                <Typography
                                                    variant='h3'
                                                    color='#15803d'
                                                    fontWeight='700'
                                                >
                                                    {locationHistory.length}
                                                </Typography>
                                                <Typography variant='caption' color='#64748b'>
                                                    Field visit records
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card
                                    elevation={0}
                                    sx={{
                                        background: agent?.suspended
                                            ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                                            : 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                        border: agent?.suspended
                                            ? '1px solid #fbbf24'
                                            : '1px solid #86efac',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: agent?.suspended
                                                ? '0 8px 24px rgba(245, 158, 11, 0.15)'
                                                : '0 8px 24px rgba(34, 197, 94, 0.15)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box display='flex' alignItems='center' gap={2}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: agent?.suspended
                                                        ? '#f59e0b'
                                                        : '#22c55e',
                                                    color: 'white'
                                                }}
                                            >
                                                <AccountCircle sx={{ fontSize: 28 }} />
                                            </Box>
                                            <Box flex={1}>
                                                <Typography
                                                    color={agent?.suspended ? '#92400e' : '#15803d'}
                                                    variant='body2'
                                                    fontWeight='600'
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    Account Status
                                                </Typography>
                                                <Typography
                                                    variant='h4'
                                                    color={agent?.suspended ? '#92400e' : '#15803d'}
                                                    fontWeight='700'
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {agent?.suspended ? 'Suspended' : 'Active'}
                                                </Typography>
                                                <Typography variant='caption' color='#64748b'>
                                                    Current account state
                                                </Typography>
                                            </Box>
                                            {agent?.suspended && (
                                                <Tooltip title='Account is suspended'>
                                                    <ErrorOutline color='warning' />
                                                </Tooltip>
                                            )}
                                            {!agent?.suspended && (
                                                <Tooltip title='Account is active'>
                                                    <CheckCircle color='success' />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Column - Tabs */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={0}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                                }}
                            >
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    sx={{
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '15px',
                                            minHeight: 60,
                                            color: '#64748b',
                                            '&.Mui-selected': {
                                                color: '#085d40'
                                            }
                                        },
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: '#085d40',
                                            height: 3,
                                            borderRadius: '3px 3px 0 0'
                                        }
                                    }}
                                >
                                    <Tab
                                        label={`Farmers (${farmers.length})`}
                                        icon={<Agriculture />}
                                        iconPosition='start'
                                    />
                                    <Tab
                                        label={`Location History (${locationHistory.length})`}
                                        icon={<Map />}
                                        iconPosition='start'
                                    />
                                </Tabs>
                            </Box>

                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflow: 'auto',
                                    p: 3,
                                    backgroundColor: '#ffffff'
                                }}
                            >
                                {/* Farmers Tab */}
                                {activeTab === 0 && (
                                    <>
                                        {farmers.length === 0 ? (
                                            <Box
                                                display='flex'
                                                flexDirection='column'
                                                alignItems='center'
                                                justifyContent='center'
                                                height='400px'
                                                sx={{
                                                    backgroundColor: '#f8fafc',
                                                    borderRadius: 2,
                                                    border: '2px dashed #cbd5e1'
                                                }}
                                            >
                                                <Agriculture
                                                    sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }}
                                                />
                                                <Typography
                                                    color='text.secondary'
                                                    align='center'
                                                    variant='h6'
                                                    gutterBottom
                                                >
                                                    No farmers registered yet
                                                </Typography>
                                                <Typography
                                                    color='text.secondary'
                                                    align='center'
                                                    variant='body2'
                                                >
                                                    This agent hasn't added any farmers to the
                                                    system.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <>
                                                <TableContainer
                                                    sx={{
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: 2,
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow
                                                                sx={{ backgroundColor: '#f8fafc' }}
                                                            >
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    No
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    Name
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    Contact
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    Location
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    Farm Products
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    Gender
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        color: '#374151'
                                                                    }}
                                                                >
                                                                    Date Created
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {farmers
                                                                .slice(
                                                                    page * rowsPerPage,
                                                                    page * rowsPerPage + rowsPerPage
                                                                )
                                                                .map((farmer, index) => (
                                                                    <TableRow
                                                                        key={farmer._id}
                                                                        sx={{
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    '#f9fafb'
                                                                            },
                                                                            '&:nth-of-type(even)': {
                                                                                backgroundColor:
                                                                                    '#fafbfc'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <TableCell>
                                                                            {page * rowsPerPage +
                                                                                index +
                                                                                1}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            sx={{ fontWeight: 500 }}
                                                                        >
                                                                            {farmer.name}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            sx={{
                                                                                fontFamily:
                                                                                    'monospace'
                                                                            }}
                                                                        >
                                                                            {farmer.contact}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <LocationDisplay
                                                                                farmer={farmer}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {farmer.farmProducts &&
                                                                            farmer.farmProducts
                                                                                .length > 0 ? (
                                                                                farmer.farmProducts.map(
                                                                                    (
                                                                                        product,
                                                                                        i
                                                                                    ) => (
                                                                                        <Chip
                                                                                            key={i}
                                                                                            label={
                                                                                                product
                                                                                            }
                                                                                            size='small'
                                                                                            sx={{
                                                                                                mr: 0.5,
                                                                                                mb: 0.5,
                                                                                                backgroundColor:
                                                                                                    '#dcfce7',
                                                                                                color: '#059669'
                                                                                            }}
                                                                                        />
                                                                                    )
                                                                                )
                                                                            ) : (
                                                                                <Typography
                                                                                    variant='body2'
                                                                                    color='text.secondary'
                                                                                >
                                                                                    N/A
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Chip
                                                                                label={
                                                                                    farmer.gender ||
                                                                                    'N/A'
                                                                                }
                                                                                size='small'
                                                                                color={
                                                                                    farmer.gender ===
                                                                                    'Male'
                                                                                        ? 'primary'
                                                                                        : farmer.gender ===
                                                                                          'Female'
                                                                                        ? 'secondary'
                                                                                        : 'default'
                                                                                }
                                                                                variant='outlined'
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            sx={{
                                                                                fontFamily:
                                                                                    'monospace',
                                                                                fontSize: '0.875rem'
                                                                            }}
                                                                        >
                                                                            {new Date(
                                                                                farmer.createdAt
                                                                            ).toLocaleDateString()}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                <TablePagination
                                                    component='div'
                                                    count={farmers.length}
                                                    page={page}
                                                    onPageChange={handleChangePage}
                                                    rowsPerPage={rowsPerPage}
                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                                    sx={{
                                                        borderTop: '1px solid #e5e7eb',
                                                        backgroundColor: '#f9fafb',
                                                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                                                            {
                                                                color: '#374151',
                                                                fontWeight: 500
                                                            }
                                                    }}
                                                />
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Location History Tab */}
                                {activeTab === 1 && (
                                    <>
                                        {/* Date Filter Section */}
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                mb: 3,
                                                border: '1px solid #e2e8f0',
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc'
                                            }}
                                        >
                                            <Typography
                                                variant='h6'
                                                fontWeight='600'
                                                sx={{ mb: 2, color: '#374151' }}
                                            >
                                                Filter by Date Range
                                            </Typography>
                                            <Grid container spacing={2} alignItems='center'>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label='Start Date'
                                                        type='date'
                                                        size='small'
                                                        fullWidth
                                                        value={dateFilter.startDate}
                                                        onChange={(e) =>
                                                            handleDateFilterChange(
                                                                'startDate',
                                                                e.target.value
                                                            )
                                                        }
                                                        InputLabelProps={{ shrink: true }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label='End Date'
                                                        type='date'
                                                        size='small'
                                                        fullWidth
                                                        value={dateFilter.endDate}
                                                        onChange={(e) =>
                                                            handleDateFilterChange(
                                                                'endDate',
                                                                e.target.value
                                                            )
                                                        }
                                                        InputLabelProps={{ shrink: true }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Button
                                                        variant='contained'
                                                        onClick={applyDateFilter}
                                                        disabled={locationLoading}
                                                        fullWidth
                                                        sx={{
                                                            backgroundColor: '#085d40',
                                                            '&:hover': {
                                                                backgroundColor: '#047857'
                                                            },
                                                            borderRadius: 2,
                                                            py: 1
                                                        }}
                                                    >
                                                        {locationLoading ? (
                                                            <CircularProgress
                                                                size={20}
                                                                color='inherit'
                                                            />
                                                        ) : (
                                                            'Apply Filter'
                                                        )}
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Button
                                                        variant='outlined'
                                                        onClick={clearDateFilter}
                                                        disabled={locationLoading}
                                                        fullWidth
                                                        sx={{ borderRadius: 2, py: 1 }}
                                                    >
                                                        Clear Filter
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Location History Content */}
                                        {locationLoading && locationHistory.length === 0 ? (
                                            <Box
                                                display='flex'
                                                justifyContent='center'
                                                alignItems='center'
                                                height='300px'
                                            >
                                                <CircularProgress
                                                    size={40}
                                                    sx={{ color: '#085d40' }}
                                                />
                                                <Typography sx={{ ml: 2, color: '#64748b' }}>
                                                    Loading location history...
                                                </Typography>
                                            </Box>
                                        ) : locationHistory.length === 0 ? (
                                            <Box
                                                display='flex'
                                                flexDirection='column'
                                                alignItems='center'
                                                justifyContent='center'
                                                height='400px'
                                                sx={{
                                                    backgroundColor: '#f8fafc',
                                                    borderRadius: 2,
                                                    border: '2px dashed #cbd5e1'
                                                }}
                                            >
                                                <Map
                                                    sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }}
                                                />
                                                <Typography
                                                    color='text.secondary'
                                                    align='center'
                                                    variant='h6'
                                                    gutterBottom
                                                >
                                                    No location history available
                                                </Typography>
                                                <Typography
                                                    color='text.secondary'
                                                    align='center'
                                                    variant='body2'
                                                >
                                                    {dateFilter.startDate || dateFilter.endDate
                                                        ? 'No location data found for the selected date range.'
                                                        : 'Location tracking data will appear here once available.'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <>
                                                <Grid container spacing={2}>
                                                    {locationHistory
                                                        .slice(
                                                            locationPage * locationRowsPerPage,
                                                            locationPage * locationRowsPerPage +
                                                                locationRowsPerPage
                                                        )
                                                        .map((location, index) => (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                key={location._id || index}
                                                            >
                                                                <LocationHistoryCard
                                                                    location={location}
                                                                    index={
                                                                        locationPage *
                                                                            locationRowsPerPage +
                                                                        index
                                                                    }
                                                                />
                                                            </Grid>
                                                        ))}
                                                </Grid>

                                                {/* Pagination for Location History */}
                                                <TablePagination
                                                    component='div'
                                                    count={locationPagination.total}
                                                    page={locationPage}
                                                    onPageChange={handleLocationChangePage}
                                                    rowsPerPage={locationRowsPerPage}
                                                    onRowsPerPageChange={
                                                        handleLocationChangeRowsPerPage
                                                    }
                                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                                    sx={{
                                                        mt: 2,
                                                        borderTop: '1px solid #e5e7eb',
                                                        backgroundColor: '#f9fafb',
                                                        borderRadius: '0 0 8px 8px',
                                                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                                                            {
                                                                color: '#374151',
                                                                fontWeight: 500
                                                            }
                                                    }}
                                                />

                                                {/* Load More Indicator */}
                                                {locationLoading && locationHistory.length > 0 && (
                                                    <Box
                                                        display='flex'
                                                        justifyContent='center'
                                                        alignItems='center'
                                                        sx={{ mt: 2, py: 2 }}
                                                    >
                                                        <CircularProgress
                                                            size={24}
                                                            sx={{ color: '#085d40' }}
                                                        />
                                                        <Typography
                                                            sx={{ ml: 2, color: '#64748b' }}
                                                        >
                                                            Loading more locations...
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Success/Error Feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant='filled'
                        sx={{
                            borderRadius: 2,
                            fontWeight: 500
                        }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* Password Reset Modal */}
                <Dialog
                    open={showPasswordModal}
                    onClose={handleClosePasswordModal}
                    maxWidth='sm'
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
                            color: 'white',
                            textAlign: 'center',
                            py: 3
                        }}
                    >
                        <Box display='flex' alignItems='center' justifyContent='center' gap={1}>
                            <VpnKey />
                            <Typography variant='h6' component='span'>
                                Reset Agent Password
                            </Typography>
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 3, pb: 2 }}>
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mb: 3, textAlign: 'center', mt: 1 }}
                        >
                            Enter a new password for agent <strong>{agent?.name}</strong>. An email
                            with the new credentials will be sent automatically.
                        </Typography>

                        <TextField
                            fullWidth
                            label='New Password'
                            type='password'
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder='Enter new password (min 6 characters)'
                            sx={{ mb: 2 }}
                            helperText='Password must be at least 6 characters long'
                        />

                        <TextField
                            fullWidth
                            label='Confirm Password'
                            type='password'
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder='Confirm new password'
                            error={confirmPassword && newPassword !== confirmPassword}
                            helperText={
                                confirmPassword && newPassword !== confirmPassword
                                    ? 'Passwords do not match'
                                    : ''
                            }
                        />
                    </DialogContent>

                    <DialogActions sx={{ p: 3, pt: 1 }}>
                        <Button
                            onClick={handleClosePasswordModal}
                            variant='outlined'
                            sx={{ mr: 1, borderRadius: 2 }}
                            disabled={passwordLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleResetPassword}
                            variant='contained'
                            disabled={passwordLoading || !newPassword || !confirmPassword}
                            sx={{
                                background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
                                borderRadius: 2,
                                minWidth: 120
                            }}
                        >
                            {passwordLoading ? (
                                <CircularProgress size={20} color='inherit' />
                            ) : (
                                'Reset Password'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default AgentDetailView;
